import { addSuppressedError } from '@shared/utils/logger';

const GUARDED: unique symbol = Symbol.for('joy.translatorDomGuard');

type GuardedMethod = Readonly<{ [GUARDED]?: true }>;

const isGuarded = (method: unknown): boolean => typeof method === 'function' && (method as GuardedMethod)[GUARDED] === true;

const isNode = (value: unknown): value is Node => typeof (value as Node | null | undefined)?.nodeType === 'number';

const isPageTranslated = (): boolean => {
  const className = typeof document === 'undefined' ? undefined : document.documentElement?.className;
  return typeof className === 'string' && /\btranslated-(ltr|rtl)\b/.test(className);
};

const findChildContaining = (parent: Node, node: Node): Node | null => {
  let current: Node | null = node;
  while (current && current.parentNode !== parent) current = current.parentNode;
  return current;
};

// Translators wrap React's text nodes in <font>, so React mutates the wrong parent: https://github.com/facebook/react/issues/11538
export const guardTranslatorDomMutations = (): void => {
  if (typeof Node === 'undefined') return;
  const proto = Node.prototype;
  if (isGuarded(proto.removeChild) || isGuarded(proto.insertBefore)) return;

  const nativeRemoveChild = proto.removeChild;
  const nativeInsertBefore = proto.insertBefore;
  let hasReported = false;

  const report = (method: 'removeChild' | 'insertBefore', parent: Node, node: Node) => {
    if (hasReported) return;
    hasReported = true;
    // Reporting is best effort: it must never turn a recovered mutation into a crash.
    try {
      addSuppressedError(new Error(`Recovered from ${method}: the node is not a child of this node`), {
        context: 'translatorDomGuard',
        method,
        parentNodeName: parent.nodeName,
        nodeName: node.nodeName,
        actualParentNodeName: node.parentNode?.nodeName ?? null,
        isPageTranslated: isPageTranslated(),
      });
    } catch {
      // Ignore.
    }
  };

  const removeChild = function <T extends Node>(this: Node, child: T): T {
    if (!isNode(child)) return nativeRemoveChild.call(this, child) as T;
    const actualParent = child.parentNode;
    if (actualParent !== this) {
      report('removeChild', this, child);
      // The translator moved the node, so remove it from where it really is to avoid leaving stale text behind.
      if (actualParent) nativeRemoveChild.call(actualParent, child);
      return child;
    }
    return nativeRemoveChild.call(this, child) as T;
  };

  const insertBefore = function <T extends Node>(this: Node, node: T, child: Node | null): T {
    if (child && isNode(child) && child.parentNode !== this) {
      report('insertBefore', this, child);
      return nativeInsertBefore.call(this, node, findChildContaining(this, child)) as T;
    }
    return nativeInsertBefore.call(this, node, child) as T;
  };

  Object.defineProperty(removeChild, GUARDED, { value: true });
  Object.defineProperty(insertBefore, GUARDED, { value: true });
  proto.removeChild = removeChild;
  proto.insertBefore = insertBefore;
};
